import { activeFilters, type ReportFilterRow } from './reportFilters';

/**
 * Applies the toolbar's search + filter rows to an ALREADY-FETCHED list.
 *
 * Ported from sellokart-cloud. Every report page here except Orders fetches
 * its whole result set for the selected date range up front, so filtering it
 * in the browser is exactly equivalent to filtering it in the query and
 * avoids threading a predicate through every report service. Orders is
 * server-paginated and applies the same semantics server-side instead (see
 * `apps/api/src/reports/report-filter.ts`).
 *
 * Semantics: case-insensitive, search ORs across its columns, filter rows
 * OR/AND per `matchAny`, and search composes with filters by AND (search
 * narrows, it does not widen).
 */

export interface ClientFilterOptions<T> {
  search: string;
  filterRows: readonly ReportFilterRow[];
  matchAny: boolean;
  /** Field name (as shown in the popover) → that row's value for it. */
  fieldValue: (row: T, field: string) => string | undefined;
  /** Values the free-text search looks across. */
  searchValues: (row: T) => ReadonlyArray<string | undefined>;
}

function matches(value: string, condition: ReportFilterRow['condition'], term: string): boolean {
  const a = value.toLowerCase();
  const b = term.trim().toLowerCase();
  switch (condition) {
    case 'equals':
      return a === b;
    case 'starts with':
      return a.startsWith(b);
    case 'ends with':
      return a.endsWith(b);
    case 'contains':
    default:
      return a.includes(b);
  }
}

export function applyClientFilters<T>(rows: readonly T[], options: ClientFilterOptions<T>): T[] {
  const term = options.search.trim().toLowerCase();
  const active = activeFilters(options.filterRows);

  return rows.filter((row) => {
    if (term !== '') {
      const hit = options
        .searchValues(row)
        .some((value) => (value ?? '').toLowerCase().includes(term));
      if (!hit) return false;
    }
    if (active.length === 0) return true;

    const test = (filter: ReportFilterRow) => {
      // A field this page doesn't expose constrains nothing, mirroring the
      // server dropping a column it never offered.
      const value = options.fieldValue(row, filter.field);
      return value === undefined ? false : matches(value, filter.condition, filter.value);
    };
    return options.matchAny ? active.some(test) : active.every(test);
  });
}
